import { useState, useEffect } from 'react';
import Button from '@components/ui/Button';
import Toggle from '@components/ui/Toggle';
import { useApiClient } from '@hooks/useApiClient';
import { toast } from '@utils/toast';
import { triggerAllUsersEvent } from '../../../services/storeEventService';
import {
    ADMIN_ALL_USERS_TRIGGER_EVENTS,
    type AdminAllUsersTriggerEventType,
} from '../../../types/api.types';
import { ModalShell } from './shared';

interface AllUsersEventModalProps {
    isOpen: boolean;
    storeBrandId: string;
    onClose: () => void;
}

const UPDATE_LABELS: Record<AdminAllUsersTriggerEventType, string> = {
    AUTO_RELOAD: 'Refresh store apps',
    UPDATE_PRINTER_SERVICE: 'Refresh receipt printers',
};

export default function AllUsersEventModal({
    isOpen,
    storeBrandId,
    onClose,
}: AllUsersEventModalProps) {
    const [selectedUpdate, setSelectedUpdate] = useState<AdminAllUsersTriggerEventType>(
        ADMIN_ALL_USERS_TRIGGER_EVENTS.AUTO_RELOAD
    );
    const [forceReload, setForceReload] = useState(false);

    const { loading, callApi } = useApiClient({ toast: true });

    useEffect(() => {
        if (!isOpen) {
            setSelectedUpdate(ADMIN_ALL_USERS_TRIGGER_EVENTS.AUTO_RELOAD);
            setForceReload(false);
        }
    }, [isOpen]);

    const handleSend = async () => {
        await callApi(
            () =>
                triggerAllUsersEvent(
                    {
                        eventName: selectedUpdate,
                        ...(selectedUpdate === 'AUTO_RELOAD' ? { forceReload } : {}),
                    },
                    storeBrandId
                ),
            () => {
                toast.success('Update sent. All store apps will receive it shortly.');
            }
        );
    };

    const footer = (
        <Button
            variant="primary"
            className="w-full"
            isLoading={loading}
            onClick={handleSend}
        >
            Send to all stores
        </Button>
    );

    return (
        <ModalShell
            isOpen={isOpen}
            title="Update all stores"
            subtitle="Send an instruction to the app on every store under this brand. Stores receive it the next time they connect."
            onClose={onClose}
            footer={footer}
            maxWidth="md"
        >
            <div className="space-y-4">
                <div>
                    <label
                        htmlFor="update-type-select"
                        className="block text-sm font-medium text-[#1e293b] mb-1.5"
                    >
                        Choose an update
                    </label>
                    <select
                        id="update-type-select"
                        value={selectedUpdate}
                        onChange={(e) =>
                            setSelectedUpdate(e.target.value as AdminAllUsersTriggerEventType)
                        }
                        className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                    >
                        {(
                            Object.keys(
                                ADMIN_ALL_USERS_TRIGGER_EVENTS
                            ) as AdminAllUsersTriggerEventType[]
                        ).map((key) => (
                            <option key={key} value={key}>
                                {UPDATE_LABELS[key]}
                            </option>
                        ))}
                    </select>
                </div>

                {selectedUpdate === 'AUTO_RELOAD' && (
                    <div className="rounded-xl border border-[#e2e8f0] px-4 py-3">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <p className="text-sm font-medium text-[#1e293b]">
                                    Full refresh
                                </p>
                                <p className="text-xs text-[#64748b] mt-0.5">
                                    Clears cached data and reloads the app from scratch. Use if a
                                    store isn&apos;t showing the latest changes.
                                </p>
                            </div>
                            <Toggle
                                label="Full refresh"
                                value={forceReload}
                                onChange={setForceReload}
                            />
                        </div>
                    </div>
                )}

            </div>
        </ModalShell>
    );
}
